import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const portraits = [
  "https://customer-assets.emergentagent.com/job_actingfolio/artifacts/argqat2o__MG_3894.JPG",
  "https://customer-assets.emergentagent.com/job_actingfolio/artifacts/9u27wus3__MG_3992.JPG",
  "https://customer-assets.emergentagent.com/job_actingfolio/artifacts/35x3hk87__MG_4127.JPG",
  "https://customer-assets.emergentagent.com/job_actingfolio/artifacts/9i9epqlg__MG_5586.JPG",
  "https://customer-assets.emergentagent.com/job_actingfolio/artifacts/vh2dezoq__MG_6040.JPG",
  "https://customer-assets.emergentagent.com/job_actingfolio/artifacts/vuinhsvf__MG_7029.JPG",
  "https://customer-assets.emergentagent.com/job_actingfolio/artifacts/cckkwx9s_20230530234028__MG_3550.JPG"
];

// Option 1: Featured + Grid Gallery
export const Option1_FeaturedGrid = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const featuredImage = portraits[0];
  const gridImages = portraits.slice(1);

  return (
    <section className="min-h-screen bg-[#1a1a1a] py-20 px-6 sm:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl font-black text-white mb-4">
            OPTION 1: <span className="text-[#FF6B6B]">FEATURED + GRID</span>
          </h2>
          <div className="w-24 h-1 bg-[#FF6B6B] mx-auto mb-6"></div>
          <p className="text-gray-400 text-lg">One hero portrait + grid below</p>
        </div>

        {/* Featured Portrait */}
        <div className="mb-12 max-w-5xl mx-auto">
          <div className="relative group cursor-pointer" onClick={() => setSelectedImage(featuredImage)}>
            <div className="absolute -inset-4 bg-gradient-to-r from-[#FF6B6B] via-[#4ECDC4] to-[#FFD700] rounded-2xl blur-2xl opacity-20 group-hover:opacity-40 transition-opacity"></div>
            <div className="relative aspect-[4/5] rounded-xl overflow-hidden">
              <img src={featuredImage} alt="Featured" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
            </div>
          </div>
        </div>

        {/* Grid of Portraits */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {gridImages.map((image, index) => (
            <div key={index} className="group relative aspect-[3/4] rounded-lg overflow-hidden cursor-pointer" onClick={() => setSelectedImage(image)}>
              <img src={image} alt={`Portrait ${index + 2}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"></div>
            </div>
          ))}
        </div>
      </div>

      {selectedImage && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
          <button className="absolute top-8 right-8 text-white hover:text-[#FF6B6B]" onClick={() => setSelectedImage(null)}>
            <X size={40} />
          </button>
          <img src={selectedImage} alt="Full" className="max-w-full max-h-[90vh] object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </section>
  );
};

// Option 2: Cinematic Carousel
export const Option2_Carousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => setCurrentIndex((prev) => (prev + 1) % portraits.length);
  const prevSlide = () => setCurrentIndex((prev) => (prev - 1 + portraits.length) % portraits.length);

  return (
    <section className="min-h-screen bg-[#0a0a0a] py-20 px-6 sm:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl font-black text-white mb-4">
            OPTION 2: <span className="text-[#4ECDC4]">CINEMATIC CAROUSEL</span>
          </h2>
          <div className="w-24 h-1 bg-[#4ECDC4] mx-auto mb-6"></div>
          <p className="text-gray-400 text-lg">Full-width slider with smooth transitions</p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          {/* Carousel */}
          <div className="relative aspect-[16/10] rounded-2xl overflow-hidden">
            <img src={portraits[currentIndex]} alt={`Slide ${currentIndex + 1}`} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            
            {/* Navigation */}
            <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-colors">
              <ChevronLeft className="text-white" size={24} />
            </button>
            <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-colors">
              <ChevronRight className="text-white" size={24} />
            </button>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {portraits.map((_, index) => (
              <button key={index} onClick={() => setCurrentIndex(index)} className={`w-3 h-3 rounded-full transition-all ${
                index === currentIndex ? 'bg-[#4ECDC4] w-8' : 'bg-gray-600'
              }`} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Option 3: Bento Grid (Asymmetric)
export const Option3_BentoGrid = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section className="min-h-screen bg-[#1a1a1a] py-20 px-6 sm:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl font-black text-white mb-4">
            OPTION 3: <span className="text-[#FFD700]">BENTO GRID</span>
          </h2>
          <div className="w-24 h-1 bg-[#FFD700] mx-auto mb-6"></div>
          <p className="text-gray-400 text-lg">Asymmetric layout with varying sizes</p>
        </div>

        <div className="grid grid-cols-4 grid-rows-3 gap-4 h-[900px]">
          {/* Large - Top Left */}
          <div className="col-span-2 row-span-2 group relative rounded-xl overflow-hidden cursor-pointer" onClick={() => setSelectedImage(portraits[0])}>
            <img src={portraits[0]} alt="Portrait 1" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>

          {/* Medium - Top Right */}
          <div className="col-span-2 row-span-1 group relative rounded-xl overflow-hidden cursor-pointer" onClick={() => setSelectedImage(portraits[1])}>
            <img src={portraits[1]} alt="Portrait 2" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
          </div>

          {/* Small - Right */}
          <div className="col-span-1 row-span-1 group relative rounded-xl overflow-hidden cursor-pointer" onClick={() => setSelectedImage(portraits[2])}>
            <img src={portraits[2]} alt="Portrait 3" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
          </div>

          <div className="col-span-1 row-span-1 group relative rounded-xl overflow-hidden cursor-pointer" onClick={() => setSelectedImage(portraits[3])}>
            <img src={portraits[3]} alt="Portrait 4" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
          </div>

          {/* Bottom Row */}
          <div className="col-span-1 row-span-1 group relative rounded-xl overflow-hidden cursor-pointer" onClick={() => setSelectedImage(portraits[4])}>
            <img src={portraits[4]} alt="Portrait 5" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
          </div>

          <div className="col-span-2 row-span-1 group relative rounded-xl overflow-hidden cursor-pointer" onClick={() => setSelectedImage(portraits[5])}>
            <img src={portraits[5]} alt="Portrait 6" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
          </div>

          <div className="col-span-1 row-span-1 group relative rounded-xl overflow-hidden cursor-pointer" onClick={() => setSelectedImage(portraits[6])}>
            <img src={portraits[6]} alt="Portrait 7" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
          </div>
        </div>
      </div>

      {selectedImage && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
          <button className="absolute top-8 right-8 text-white hover:text-[#FFD700]" onClick={() => setSelectedImage(null)}>
            <X size={40} />
          </button>
          <img src={selectedImage} alt="Full" className="max-w-full max-h-[90vh] object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </section>
  );
};

// Option 4: Split-Screen Dual Display
export const Option4_SplitScreen = () => {
  const pairs = [
    [portraits[0], portraits[1]],
    [portraits[2], portraits[3]],
    [portraits[4], portraits[5]]
  ];
  const [currentPair, setCurrentPair] = useState(0);

  return (
    <section className="min-h-screen bg-[#0a0a0a] py-20 px-6 sm:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl font-black text-white mb-4">
            OPTION 4: <span className="text-[#FF6B6B]">SPLIT-SCREEN</span>
          </h2>
          <div className="w-24 h-1 bg-[#FF6B6B] mx-auto mb-6"></div>
          <p className="text-gray-400 text-lg">Two contrasting looks side by side</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 h-[700px]">
          <div className="relative group rounded-xl overflow-hidden">
            <img src={pairs[currentPair][0]} alt="Left" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#FF6B6B]/20 to-transparent"></div>
          </div>
          <div className="relative group rounded-xl overflow-hidden">
            <img src={pairs[currentPair][1]} alt="Right" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-l from-[#4ECDC4]/20 to-transparent"></div>
          </div>
        </div>

        <div className="flex justify-center gap-3 mt-12">
          {pairs.map((_, index) => (
            <button key={index} onClick={() => setCurrentPair(index)} className={`px-6 py-2 rounded-full font-medium transition-all ${
              index === currentPair ? 'bg-[#FF6B6B] text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}>
              Pair {index + 1}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

// Option 5: Masonry/Pinterest Style
export const Option5_Masonry = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section className="min-h-screen bg-[#1a1a1a] py-20 px-6 sm:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl font-black text-white mb-4">
            OPTION 5: <span className="text-[#4ECDC4]">MASONRY</span>
          </h2>
          <div className="w-24 h-1 bg-[#4ECDC4] mx-auto mb-6"></div>
          <p className="text-gray-400 text-lg">Pinterest-style flowing layout</p>
        </div>

        <div className="columns-2 md:columns-3 gap-6 space-y-6">
          {portraits.map((image, index) => (
            <div key={index} className="break-inside-avoid group relative rounded-lg overflow-hidden cursor-pointer mb-6" onClick={() => setSelectedImage(image)}>
              <img src={image} alt={`Portrait ${index + 1}`} className="w-full group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"></div>
            </div>
          ))}
        </div>
      </div>

      {selectedImage && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
          <button className="absolute top-8 right-8 text-white hover:text-[#4ECDC4]" onClick={() => setSelectedImage(null)}>
            <X size={40} />
          </button>
          <img src={selectedImage} alt="Full" className="max-w-full max-h-[90vh] object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </section>
  );
};
